import {
	Button,
	Checkbox,
	Dimmer,
	type Job,
	Label,
	Loader,
	Message,
	Segment,
	TraitementLong,
} from '@mairistem/composants';
import {
	formatMontant,
	getErrorMessage,
	type MetaRequest,
} from '@mairistem/finances-utils';
import { isEqual, sumBy } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import StatutMontantSimulation from '../../constants/simulation/statutMontantSimulation';
import type TypePeriode from '../../constants/simulation/typePeriode';
import type {
	ArbitrageMassePayload,
	ResultatArbitrageMasse,
	SelectionArbitrageMasse,
	StatutArbitrage,
} from '../../interfaces/simulation/arbitrageMasse';
import type DetailMontantSimulation from '../../interfaces/simulation/detailMontantSimulation';
import type FiltresArbitrageMasse from '../../interfaces/simulation/filtresArbitrageMasse';
import type MontantSimulation from '../../interfaces/simulation/montantSimulation';
import type Simulation from '../../interfaces/simulation/simulation';
import { lancerArbitrageMasse } from '../../services/simulation/montantSimulation';
import {
	getDetailMontants,
	lancerDetailMontants,
} from '../../services/simulation/simulation';
import {
	fetchIsDemandeEnabled,
	getDefaultPeriode,
	isArbitrageMasseDisponible,
} from '../../utils/simulation/simulation';
import { renseignerNomUtilisateur } from '../../utils/simulation/utilisateur';
import {
	chargerSimulationCourante,
	useSimulationCourante,
} from '../common/Context/simulationStore';
import { useUserRoleCourant } from '../common/Context/userRoleStore';
import './ArbitrageMasse.css';
import ArbitrageMasseBarreActions from './ArbitrageMasseBarreActions';
import ArbitrageMasseFiltres from './ArbitrageMasseFiltres';
import ArbitrageMasseListe from './ArbitrageMasseListe';

const PER_PAGE = 15;

const FILTRES_PAR_DEFAUT: FiltresArbitrageMasse = {
	codesAnalytique: [],
	codesArticle: [],
	codesChapitre: [],
	codesFonction: [],
	codesOperation: [],
	codesVentilation: [],
	identifiantsEtiquette: [],
	identifiantsLogin: [],
	identifiantsService: [],
	inclureTraitees: false,
	montantMax: null,
	montantMin: null,
	recherche: '',
	sections: [],
	sens: [],
};

const isAArbitrer = (montant: Pick<MontantSimulation, 'statut'>) =>
	montant.statut === StatutMontantSimulation.A_ARBITRER;

const selectionVide = (): SelectionArbitrageMasse => ({
	identifiants: new Set(),
	tout: false,
});

// Le resultat d'un job peut arriver serialise en JSON
const lireResultatArbitrage = (
	resultat: unknown,
): ResultatArbitrageMasse | null => {
	try {
		const valeur =
			typeof resultat === 'string' ? JSON.parse(resultat) : resultat;

		return typeof valeur?.traitees === 'number' ? valeur : null;
	} catch {
		return null;
	}
};

interface ArbitrageMasseProps {
	identifiantSimulation: number;
}

const ArbitrageMasse = ({ identifiantSimulation }: ArbitrageMasseProps) => {
	const userRole = useUserRoleCourant();
	const simulationCourante = useSimulationCourante();
	const [periodeParDefaut, setPeriodeParDefaut] =
		React.useState<TypePeriode>(null);
	const simulation = React.useMemo<Simulation>(() => {
		const typePeriode = simulationCourante?.typePeriode ?? periodeParDefaut;

		// La periode DEMANDE vaut 0 : on ne peut pas tester sa simple veracite
		return simulationCourante && typePeriode != null
			? { ...simulationCourante, typePeriode }
			: null;
	}, [simulationCourante, periodeParDefaut]);
	const [filtres, setFiltres] =
		React.useState<FiltresArbitrageMasse>(FILTRES_PAR_DEFAUT);
	// Job en cours de calcul (null une fois termine)
	const [job, setJob] = React.useState<Job>(null);
	// Job termine avec succes, dont on lit le resultat page par page
	const [identifiantJob, setIdentifiantJob] = React.useState<number>(null);
	const [montants, setMontants] = React.useState<DetailMontantSimulation[]>([]);
	const [meta, setMeta] = React.useState<
		MetaRequest & { totalDemande?: number }
	>(null);
	const [selection, setSelection] =
		React.useState<SelectionArbitrageMasse>(selectionVide);
	// Job d'arbitrage en masse en cours
	const [jobArbitrage, setJobArbitrage] = React.useState<Job>(null);
	// Compteurs sur tout le resultat du job de detail, sans filtre
	const [compteursJob, setCompteursJob] = React.useState<{
		aArbitrer: number;
		total: number;
	} | null>(null);
	// Lignes supprimees depuis le job de detail : elles restent dans son
	// resultat, et l'API ferait echouer tout un arbitrage "Tout selectionner"
	// qui les inclurait (ligne qui n'existe plus) ; on les exclut donc
	const [supprimeesDepuisJob, setSupprimeesDepuisJob] = React.useState<
		Set<number>
	>(() => new Set());
	// Numero de la page en cours de chargement (null si aucun chargement)
	const [pageEnChargement, setPageEnChargement] = React.useState<number>(null);
	// Numero de la derniere requete envoyee : une reponse plus ancienne (ex:
	// filtre modifie entre-temps) est ignoree
	const derniereRequete = React.useRef(0);
	const repereFinListe = React.useRef<HTMLDivElement>(null);

	const handleLancerJob = async (idSimulation: number) => {
		try {
			setIdentifiantJob(null);
			setMeta(null);
			setSelection(selectionVide());
			setSupprimeesDepuisJob(new Set());
			setCompteursJob(null);
			setJob(await lancerDetailMontants(idSimulation));
		} catch (e) {
			toast.error(getErrorMessage(e, 'Erreur lors du chargement des demandes'));
		}
	};

	// Simulation deja chargee par AppSimulation (ou publiee par l'ecran
	// d'elaboration) : on ne la recharge que si elle n'est pas encore connue
	React.useEffect(() => {
		chargerSimulationCourante(identifiantSimulation).catch((e) => {
			toast.error(
				getErrorMessage(e, 'Erreur lors du chargement de la simulation'),
			);
		});
	}, [identifiantSimulation]);

	// Meme periode que la modale des montants : celle affichee dans l'ecran
	// d'elaboration (publiee avec sa simulation), sinon la periode par defaut
	React.useEffect(() => {
		if (!simulationCourante || simulationCourante.typePeriode != null) return;

		let actif = true;

		fetchIsDemandeEnabled(simulationCourante)
			.then((demandeEnabled) => {
				if (actif) {
					setPeriodeParDefaut(
						getDefaultPeriode(simulationCourante, demandeEnabled),
					);
				}
			})
			.catch((e) => {
				toast.error(
					getErrorMessage(e, 'Erreur lors du chargement de la simulation'),
				);
			});

		return () => {
			actif = false;
		};
	}, [simulationCourante]);

	const identifiantSimulationCourante = simulation?.identifiant;
	const disponible = isArbitrageMasseDisponible(simulation, userRole);

	// Un seul job par ouverture de l'ecran (et non a chaque mise a jour de la
	// simulation partagee)
	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin de 'handleLancerJob'
	React.useEffect(() => {
		if (disponible) handleLancerJob(identifiantSimulationCourante);
	}, [identifiantSimulationCourante, disponible]);

	// Le resultat du job est fige : les pages suivantes s'ajoutent a la suite
	// des lignes deja chargees sans risque de decalage. La page 1 (nouveau job
	// ou filtres modifies) remplace la liste.
	const fetchPage = async (
		idJob: number,
		page: number,
		filtresPage: FiltresArbitrageMasse,
	) => {
		derniereRequete.current += 1;
		const numeroRequete = derniereRequete.current;

		try {
			setPageEnChargement(page);
			const reponse = await getDetailMontants(
				identifiantSimulation,
				idJob,
				page,
				PER_PAGE,
				filtresPage,
			);
			if (numeroRequete !== derniereRequete.current) return;

			reponse.data.forEach((montant) => {
				renseignerNomUtilisateur(
					montant.identifiantLogin,
					montant.prenomUtilisateur,
					montant.nomUtilisateur,
				);
			});
			setMontants((old) =>
				page === 1 ? reponse.data : [...old, ...reponse.data],
			);
			setMeta(reponse.meta);
		} catch (e) {
			if (numeroRequete === derniereRequete.current) {
				toast.error(
					getErrorMessage(e, 'Erreur lors du chargement des demandes'),
				);
			}
		} finally {
			if (numeroRequete === derniereRequete.current) {
				setPageEnChargement(null);
			}
		}
	};

	// Un changement de filtre relit le resultat du job depuis la page 1, sans
	// relancer le job
	// biome-ignore lint/correctness/useExhaustiveDependencies: seul un changement de filtre doit relancer la lecture
	React.useEffect(() => {
		// Le perimetre change : la selection n'a plus de sens
		setSelection(selectionVide());
		if (identifiantJob) fetchPage(identifiantJob, 1, filtres);
	}, [filtres]);

	const hasPageSuivante = Boolean(
		identifiantJob && meta && meta.currentPage < meta.lastPage,
	);

	// Chargement de la page suivante quand le bas de la liste devient visible.
	// Le root par defaut (viewport) evite de dependre du conteneur qui porte
	// l'ascenseur (celui du Shell)
	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin de 'fetchPage'
	React.useEffect(() => {
		const repere = repereFinListe.current;
		if (!repere || !hasPageSuivante || pageEnChargement !== null) {
			return undefined;
		}

		const observer = new IntersectionObserver(
			([entree]) => {
				if (entree.isIntersecting) {
					observer.disconnect();
					fetchPage(identifiantJob, meta.currentPage + 1, filtres);
				}
			},
			{ rootMargin: '200px' },
		);
		observer.observe(repere);

		return () => {
			observer.disconnect();
		};
	}, [filtres, hasPageSuivante, identifiantJob, meta, pageEnChargement]);

	// Compteurs sur tout le resultat du job, quels que soient les filtres :
	// meme route que la liste, sans filtre et avec une seule ligne par page,
	// dont seul meta.total est lu (tous statuts, puis a arbitrer seulement)
	const fetchCompteursJob = async (idJob: number) => {
		try {
			const [toutes, aArbitrer] = await Promise.all([
				getDetailMontants(identifiantSimulation, idJob, 1, 1, {
					...FILTRES_PAR_DEFAUT,
					inclureTraitees: true,
				}),
				getDetailMontants(
					identifiantSimulation,
					idJob,
					1,
					1,
					FILTRES_PAR_DEFAUT,
				),
			]);
			setCompteursJob({
				aArbitrer: aArbitrer?.meta?.total ?? 0,
				total: toutes?.meta?.total ?? 0,
			});
		} catch (e) {
			toast.error(
				getErrorMessage(e, 'Erreur lors du chargement des compteurs'),
			);
		}
	};

	const handleFinishJob = (finishedJob: Job) => {
		setJob(null);

		if (finishedJob.status === 1) {
			setIdentifiantJob(finishedJob.identifiant);
			fetchPage(finishedJob.identifiant, 1, filtres);
			fetchCompteursJob(finishedJob.identifiant);
		} else {
			toast.error(finishedJob.resultat);
		}
	};

	const handleJobError = () => {
		setJob(null);
		toast.error('Erreur lors du traitement');
	};

	const handleChangeFiltres = (changes: Partial<FiltresArbitrageMasse>) => {
		setFiltres((old) => ({ ...old, ...changes }));
	};

	const filtresModifies = !isEqual(filtres, FILTRES_PAR_DEFAUT);

	const handleReinitialiserFiltres = () => {
		setFiltres(FILTRES_PAR_DEFAUT);
	};

	// Les compteurs du job sont ajustes localement apres une modification faite
	// sur cette page (le resultat du job, lui, est fige)
	const modifierCompteursJob = (deltaTotal: number, deltaAArbitrer: number) => {
		if (deltaTotal === 0 && deltaAArbitrer === 0) return;

		setCompteursJob(
			(old) =>
				old && {
					aArbitrer: old.aArbitrer + deltaAArbitrer,
					total: old.total + deltaTotal,
				},
		);
	};

	// Le total (et la somme demandee) du perimetre filtre suit les lignes
	// ajoutees ou supprimees sur cette page
	const modifierMeta = (deltaTotal: number, deltaDemande: number) => {
		setMeta(
			(old) =>
				old && {
					...old,
					total: old.total + deltaTotal,
					...(old.totalDemande !== undefined
						? { totalDemande: old.totalDemande + deltaDemande }
						: {}),
				},
		);
	};

	// updateMontant renvoie un MontantSimulation brut : on conserve les codes
	// d'imputation, le service et le nom de l'utilisateur de la ligne d'origine
	const handleUpdateMontant = (montant: MontantSimulation) => {
		const ancienne = montants.find(
			(ligne) => ligne.identifiant === montant.identifiant,
		);
		// Arbitrage unitaire : le compteur "a arbitrer" suit le changement
		if (ancienne) {
			modifierCompteursJob(
				0,
				Number(isAArbitrer(montant)) - Number(isAArbitrer(ancienne)),
			);
		}

		setMontants((old) =>
			old.map((ligne) =>
				ligne.identifiant === montant.identifiant
					? { ...ligne, ...montant }
					: ligne,
			),
		);
	};

	// La ligne dupliquee est inseree juste apres sa ligne d'origine (meme
	// imputation, meme service) ; son auteur est l'utilisateur courant, dont le
	// nom est resolu a partir de identifiantLogin
	const handleDuplicateMontant = (
		origine: DetailMontantSimulation,
		duplique: MontantSimulation,
	) => {
		setMontants((old) => {
			const index = old.findIndex(
				(ligne) => ligne.identifiant === origine.identifiant,
			);

			return [
				...old.slice(0, index + 1),
				{
					...origine,
					...duplique,
					nomUtilisateur: null,
					prenomUtilisateur: null,
				},
				...old.slice(index + 1),
			];
		});
		// La copie est toujours creee a arbitrer
		modifierCompteursJob(1, 1);
		modifierMeta(1, duplique.demande ?? 0);
		// La copie ne fait pas partie du resultat du job : avec "Tout
		// selectionner", l'API ne l'arbitrerait pas, on l'affiche donc decochee
		if (selection.tout) {
			setSelection((old) => ({
				...old,
				identifiants: new Set(old.identifiants).add(duplique.identifiant),
			}));
		}
	};

	const handleDeleteMontant = (identifiant: number) => {
		const ligneSupprimee = montants.find(
			(ligne) => ligne.identifiant === identifiant,
		);
		const demande = ligneSupprimee?.demande ?? 0;

		modifierCompteursJob(
			-1,
			ligneSupprimee && isAArbitrer(ligneSupprimee) ? -1 : 0,
		);

		setMontants((old) =>
			old.filter((ligne) => ligne.identifiant !== identifiant),
		);
		modifierMeta(-1, -demande);
		setSelection((old) => {
			const identifiants = new Set(old.identifiants);
			identifiants.delete(identifiant);

			return { ...old, identifiants };
		});
		setSupprimeesDepuisJob((old) => new Set(old).add(identifiant));
	};

	// Memes lignes que celles dont le statut est modifiable unitairement dans
	// la carte : rien apres transfert en compta, et une ligne APCP n'est
	// jamais arbitrable (le CA/CFU n'a pas acces a la page)
	const selectionPossible = !simulation?.transfertCpta;

	const isSelectionnable = (montant: DetailMontantSimulation) =>
		(montant.identifiantProImputations ?? 0) === 0;

	const isSelectionne = (montant: DetailMontantSimulation) =>
		selection.tout !== selection.identifiants.has(montant.identifiant);

	// En mode "Tout selectionner", identifiants contient les lignes decochees
	const handleToggleSelection = (montant: DetailMontantSimulation) => {
		setSelection((old) => {
			const identifiants = new Set(old.identifiants);
			if (!identifiants.delete(montant.identifiant)) {
				identifiants.add(montant.identifiant);
			}

			return { ...old, identifiants };
		});
	};

	const toutSelectionne = selection.tout && selection.identifiants.size === 0;

	const nombreSelectionnees = selection.tout
		? (meta?.total ?? 0) - selection.identifiants.size
		: selection.identifiants.size;

	const getDemande = (montant: DetailMontantSimulation) => montant.demande ?? 0;

	const sommeDemande = (identifiants: Set<number>) =>
		sumBy(
			montants.filter((montant) => identifiants.has(montant.identifiant)),
			getDemande,
		);

	// Fourni par l'API ; a defaut, calculable seulement une fois toutes les
	// lignes chargees
	const totalDemandeFiltre =
		meta?.totalDemande ??
		(meta && !hasPageSuivante ? sumBy(montants, getDemande) : null);

	// Les lignes cochees (ou decochees en mode "Tout selectionner") sont
	// forcement chargees
	const totalDemandeSelection = selection.tout
		? totalDemandeFiltre === null
			? null
			: totalDemandeFiltre - sommeDemande(selection.identifiants)
		: sommeDemande(selection.identifiants);

	const handleArbitrer = async (statut: StatutArbitrage) => {
		const base = {
			identifiantSimulation: simulation.identifiant,
			statut,
			typePeriode: simulation.typePeriode,
		};
		const { recherche, ...filtresSansRecherche } = filtres;
		const payload: ArbitrageMassePayload = selection.tout
			? {
					...base,
					filtres: { ...filtresSansRecherche, q: recherche.trim() },
					identifiantJob,
					identifiantsExclus: [
						...selection.identifiants,
						...supprimeesDepuisJob,
					],
				}
			: { ...base, identifiants: [...selection.identifiants] };

		try {
			setJobArbitrage(await lancerArbitrageMasse(payload));
		} catch (e) {
			toast.error(getErrorMessage(e, "Erreur lors de l'arbitrage"));
		}
	};

	const handleFinishArbitrage = (finishedJob: Job) => {
		setJobArbitrage(null);

		if (finishedJob.status !== 1) {
			toast.error(finishedJob.resultat);
			return;
		}

		const resultat = lireResultatArbitrage(finishedJob.resultat);
		if (resultat) {
			toast.success(
				`${resultat.traitees} demande${resultat.traitees > 1 ? 's' : ''} arbitrée${resultat.traitees > 1 ? 's' : ''}${
					resultat.ignorees > 0
						? `, ${resultat.ignorees} ignorée${resultat.ignorees > 1 ? 's' : ''}`
						: ''
				}`,
			);
		} else {
			toast.success('Arbitrage effectué');
		}

		// Le resultat du job de detail est fige : on le recalcule pour afficher
		// les nouveaux statuts
		handleLancerJob(simulation.identifiant);
	};

	const handleArbitrageError = () => {
		setJobArbitrage(null);
		toast.error('Erreur lors du traitement');
	};

	if (!simulation) {
		return (
			<Segment basic secondary>
				<Segment className="arbitrageMasseChargement" loading />
			</Segment>
		);
	}

	// Garde-fou si l'URL est ouverte directement (le menu n'est affiche que
	// dans ce cas)
	if (!isArbitrageMasseDisponible(simulation, userRole)) {
		return (
			<Segment basic secondary>
				<Message
					content="L'arbitrage de masse est réservé aux administrateurs et chargés de pilotage, et n'est pas disponible pour un CA/CFU."
					data-testid="arbitrageMasseIndisponible"
					warning
				/>
			</Segment>
		);
	}

	// Voile sur la liste quand elle est (re)chargee depuis la page 1 ; les
	// pages suivantes s'annoncent par le loader en bas de liste
	const chargementListe = pageEnChargement === 1;

	return (
		<Segment basic className="arbitrageMasse" secondary>
			<Segment>
				<ArbitrageMasseFiltres
					filtres={filtres}
					identifiantJob={identifiantJob}
					onChange={handleChangeFiltres}
					simulation={simulation}
				/>
			</Segment>

			<div className="arbitrageMasseBarre">
				<div className="arbitrageMasseBarreGauche">
					{selectionPossible && meta?.total > 0 && (
						<Checkbox
							checked={toutSelectionne}
							data-testid="arbitrageMasseToutSelectionner"
							indeterminate={nombreSelectionnees > 0 && !toutSelectionne}
							label="Tout sélectionner"
							onChange={() =>
								setSelection(
									toutSelectionne
										? selectionVide()
										: { identifiants: new Set(), tout: true },
								)
							}
						/>
					)}
					<span data-testid="arbitrageMasseCompteur">
						{meta &&
							`${meta.total} demande${meta.total > 1 ? 's' : ''} affichée${meta.total > 1 ? 's' : ''}${
								compteursJob ? ` sur ${compteursJob.total}` : ''
							}`}
					</span>
				</div>
				<div className="arbitrageMasseBarreDroite">
					{compteursJob && (
						<span
							className="arbitrageMasseBarreCompteur"
							data-testid="arbitrageMasseCompteurAArbitrer"
						>
							<Label circular color="teal" content={compteursJob.aArbitrer} />
							{`à arbitrer au total`}
						</span>
					)}
					{compteursJob && totalDemandeFiltre !== null && (
						<span aria-hidden>|</span>
					)}
					{totalDemandeFiltre !== null && (
						<span
							className="arbitrageMasseBarreCompteur"
							data-testid="arbitrageMasseTotalDemande"
						>
							Total demandé (filtré)
							<strong>{formatMontant(totalDemandeFiltre)}</strong>
						</span>
					)}
					<Button
						basic
						content="Réinitialiser les filtres"
						data-testid="arbitrageMasseReinitialiserFiltres"
						disabled={!filtresModifies}
						icon="undo"
						onClick={handleReinitialiserFiltres}
						size="small"
					/>
					<Button
						basic
						content="Actualiser"
						data-testid="arbitrageMasseActualiser"
						disabled={
							Boolean(job) || Boolean(jobArbitrage) || pageEnChargement !== null
						}
						icon="refresh"
						onClick={() => handleLancerJob(simulation.identifiant)}
						size="small"
					/>
				</div>
			</div>

			{selectionPossible && nombreSelectionnees > 0 && (
				<ArbitrageMasseBarreActions
					loading={Boolean(jobArbitrage)}
					nombreSelectionnees={nombreSelectionnees}
					onArbitrer={handleArbitrer}
					onEffacerSelection={() => setSelection(selectionVide())}
					totalDemandeFiltre={totalDemandeFiltre}
					totalDemandeSelection={totalDemandeSelection}
				/>
			)}

			<Dimmer.Dimmable
				className="arbitrageMasseContenu"
				dimmed={chargementListe}
			>
				<Dimmer active={chargementListe} inverted>
					<Loader />
				</Dimmer>
				{identifiantJob && meta && (
					<ArbitrageMasseListe
						isSelectionnable={selectionPossible ? isSelectionnable : null}
						isSelectionne={isSelectionne}
						montants={montants}
						onDeleteMontant={handleDeleteMontant}
						onDuplicateMontant={handleDuplicateMontant}
						onToggleSelection={handleToggleSelection}
						onUpdateMontant={handleUpdateMontant}
						simulation={simulation}
						userRole={userRole}
					/>
				)}
			</Dimmer.Dimmable>

			{hasPageSuivante && (
				<div
					className="arbitrageMasseFinListe"
					data-testid="arbitrageMasseFinListe"
					ref={repereFinListe}
				>
					<Loader
						active={pageEnChargement > 1}
						inline="centered"
						size="small"
					/>
				</div>
			)}

			{job && (
				<TraitementLong
					title="Chargement des demandes"
					identifiantJob={job.identifiant}
					onFinish={handleFinishJob}
					onError={handleJobError}
					indeterminate
				/>
			)}

			{jobArbitrage && (
				<TraitementLong
					title="Arbitrage des demandes"
					identifiantJob={jobArbitrage.identifiant}
					onFinish={handleFinishArbitrage}
					onError={handleArbitrageError}
					indeterminate
				/>
			)}
		</Segment>
	);
};

export default ArbitrageMasse;
